import React from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from '@clareefai/ui';
import { useTranslation } from 'react-i18next';
import { useAbility, Action, Subject } from '../../contexts/AbilityContext';
import { useAuth } from '../../contexts/AuthContext';
import { vendorApi } from '../../store/vendorApi';

interface PermissionGuardProps {
  action?: Action;
  subject?: Subject;
  vendorOnly?: boolean;
  teamMemberOnly?: boolean;
  fallback?: string;
  children: React.ReactNode;
}

/**
 * Route-level permission guard. Renders children only when the caller has
 * the required CASL ability (and/or is a vendor when vendorOnly is set,
 * or is a team member when teamMemberOnly is set).
 * Redirects to `fallback` (default "/dashboard") otherwise and shows a toast
 * so the denial isn't silent (previously a user clicking a button they
 * shouldn't have seen would just get bounced to /dashboard with no feedback).
 */
const PermissionGuard: React.FC<PermissionGuardProps> = ({
  action,
  subject,
  vendorOnly = false,
  teamMemberOnly = false,
  fallback = '/dashboard',
  children,
}) => {
  const { t } = useTranslation();
  const ability = useAbility();
  const { isTeamMember, isAuthenticated } = useAuth();
  const { data: permissionsData, isLoading } = vendorApi.useGetPermissionsQuery(undefined, {
    skip: !isAuthenticated,
  });

  if (isLoading || !permissionsData) return null;

  const deny = (reason: string) => {
    toast.error(t('shared.permissionGuard.deniedPrefix', { reason }));
    return <Navigate to={fallback} replace />;
  };

  if (vendorOnly && isTeamMember) {
    return deny(t('shared.permissionGuard.vendorOnly'));
  }

  if (teamMemberOnly && !isTeamMember) {
    return deny(t('shared.permissionGuard.teamMemberOnly'));
  }

  if (action && subject && !ability.can(action, subject)) {
    return deny(t('shared.permissionGuard.insufficient', { action, subject }));
  }

  return <>{children}</>;
};

export default PermissionGuard;
